<div class="pl-root">

  <!-- ── HEADER ROW ─────────────────────────────────────────────────────────── -->
  <div class="pl-header" *ngIf="!detailOnlyMode">
    <h2 class="pl-title">{{ 'payments.list.title' | translate }}</h2>

    <div class="pl-header-actions">
      <ws-searchbar [config]="searchConfig" [searchFunction]="dummySearchFunction" [resultMapper]="dummyResultMapper"
        (submit)="onSearch($event)" (searchTextChanged)="onSearchTextChange($event)">
      </ws-searchbar>
      <ws-button (click)="openFilters()" [matBadge]="activeFiltersCount" [tooltip]="'Filter' | translate">
        <mat-icon>filter_alt</mat-icon>
      </ws-button>
      <span [wsTooltip]="isExportDisabled ? ('payments.list.exportNoData' | translate) : ('Export' | translate)">
        <ws-actions
          class="actions-wrapper-ws-button"
          icon="download"
          [options]="exportFormatActions"
          [tooltip]="'Export' | translate"
          [disabled]="isExportDisabled"
          (clickOnAction)="onExportFormatSelected($event)">
        </ws-actions>
      </span>
    </div>
  </div>

  <!-- ── TABLE SECTION ──────────────────────────────────────────────────────── -->
  <div class="pl-table-wrap" *ngIf="!detailOnlyMode">
    <div class="pl-loading" *ngIf="loading">
      <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
      <span>{{ 'payments.list.loading' | translate }}</span>
    </div>

    <div class="pl-empty" *ngIf="!loading && payments.length === 0">
      <mat-icon>payment</mat-icon>
      <span>{{ 'payments.list.noResults' | translate }}</span>
    </div>

    <table mat-table [dataSource]="payments" class="pl-table" *ngIf="!loading && payments.length > 0">

      <!-- Data column -->
      <ng-container matColumnDef="created">
        <th mat-header-cell *matHeaderCellDef class="col-date col-date--sortable"
          (click)="toggleDateSort($event)" [attr.aria-sort]="createdSortDir === 'desc' ? 'descending' : 'ascending'">
          {{ 'payments.list.colDate' | translate }}
          <mat-icon class="col-sort-icon">{{ createdSortDir === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        </th>
        <td mat-cell *matCellDef="let p" class="col-date">
          {{ formatDate(p.created) }}
        </td>
      </ng-container>

      <!-- Stato column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef class="col-status">
          {{ 'payments.list.colStatus' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-status">
          <ws-chip [type]="statusChipType(p)" [label]="statusLabel(p)"></ws-chip>
        </td>
      </ng-container>

      <!-- Customer column -->
      <ng-container matColumnDef="customer">
        <th mat-header-cell *matHeaderCellDef class="col-customer">
          {{ 'payments.list.colCustomer' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-customer">
          <span class="customer-name">{{ p.customerName || '—' }}</span>
          <span class="customer-email" *ngIf="p.customerEmail">{{ p.customerEmail }}</span>
        </td>
      </ng-container>

      <!-- ID Vendita column -->
      <ng-container matColumnDef="saleId">
        <th mat-header-cell *matHeaderCellDef class="col-saleid">
          {{ 'payments.list.colSaleId' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-saleid">
          <span
            class="saleid-uuid"
            *ngIf="isRealId(p.saleId)"
            [matTooltip]="p.saleId">{{ adminIdDisplay(p.saleId) }}</span>
          <span
            class="saleid-uuid"
            *ngIf="!isRealId(p.saleId) && isRealId(p.entityId)"
            [matTooltip]="p.entityId">{{ adminIdDisplay(p.entityId) }}</span>
          <span class="saleid-type" *ngIf="p.entityType">{{ entityTypeLabel(p.entityType) }}</span>
          <span *ngIf="!isRealId(p.saleId) && !isRealId(p.entityId) && !p.entityType">—</span>
        </td>
      </ng-container>

      <!-- Modalità column -->
      <ng-container matColumnDef="mode">
        <th mat-header-cell *matHeaderCellDef class="col-mode">
          {{ 'payments.list.colMode' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-mode">
          <span class="pl-mode-cell">
            <img *ngIf="paymentBrandIcon(p) as icon" class="pl-method-icon" [src]="icon" alt="" />
            {{ transactionModeLabel(p) }}
          </span>
        </td>
      </ng-container>

      <!-- Comm. WS column -->
      <ng-container matColumnDef="commWs">
        <th mat-header-cell *matHeaderCellDef class="col-comm">
          {{ 'payments.list.colCommWs' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-comm">
          {{ formatFeeCents(p.commissioneWspaymentsCents || 0, p.currency) }}
        </td>
      </ng-container>

      <!-- Comm. Divinea column -->
      <ng-container matColumnDef="commDivinea">
        <th mat-header-cell *matHeaderCellDef class="col-comm">
          {{ 'payments.list.colCommDivinea' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-comm">
          {{ formatFeeCents(p.commissioneDivineaComCents || 0, p.currency) }}
        </td>
      </ng-container>

      <!-- Importo column -->
      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef class="col-amount">
          {{ 'payments.list.colAmount' | translate }}
        </th>
        <td mat-cell *matCellDef="let p" class="col-amount">
          <span class="amount-value" [class.amount-value--refunded]="p.displayStatus === 'refunded'">
            {{ formatCents(p.amountCents, p.currency) }}
          </span>
          <span class="amount-refunded" *ngIf="p.partialRefund">
            -{{ formatCents(p.amountRefundedCents, p.currency) }}
          </span>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let p; columns: displayedColumns" class="pl-row" (click)="openDetail(p)"></tr>
    </table>
  </div>

  <!-- ── PAGINATOR ──────────────────────────────────────────────────────────── -->
  <div class="pl-paginator-wrap" *ngIf="!loading && !detailOnlyMode">
    <mat-paginator [length]="totalElements" [pageIndex]="pageIndex" [pageSize]="pageSize"
      [pageSizeOptions]="[10, 25, 50, 100]" (page)="onPageChange($event)">
    </mat-paginator>
  </div>

</div>

<!-- ═══════════════════════════════════════════════════════════════════════════
     DETAIL MODAL OVERLAY
  ═══════════════════════════════════════════════════════════════════════════ -->
<div
  class="pl-overlay"
  *ngIf="detailOpen && selectedPayment"
  [class.pl-overlay--embedded]="detailOnlyMode"
  (click)="detailOnlyMode ? null : closeDetail()">
<div class="pl-detail-modal" role="dialog" (click)="$event.stopPropagation()"
  [attr.aria-label]="'payments.list.detailTitle' | translate">
  <!-- Close -->
  <button class="pl-modal-close" (click)="closeDetail()">
    <mat-icon>close</mat-icon>
  </button>

  <!-- ── Modal header (fixed) ─────────────────────────────────────────────── -->
  <div class="pl-detail-header">
    <div class="pl-detail-header-top">
      <div class="pl-detail-amount-row">
        <span class="pl-detail-amount">{{ formatCents(selectedPayment.amountCents, selectedPayment.currency) }}</span>
        <span class="pl-detail-currency">{{ selectedPayment.currency | uppercase }}</span>
      </div>
      <ws-chip class="pl-header-chip" [type]="statusChipType(selectedPayment)"
        [label]="statusLabel(selectedPayment)"></ws-chip>
    </div>
    <div class="pl-detail-date" *ngIf="paymentHeaderEmail(selectedPayment) as email">
      {{ 'payments.list.detailHeaderFrom' | translate }} {{ email }}
    </div>

    <div class="pl-detail-action-area">
      <!-- Riuscito, non rimborsato -->
      <ws-button *ngIf="selectedPayment.displayStatus === 'succeeded' && !selectedPayment.partialRefund"
        [callToActionType]="'primary'" [width]="'100%'"
        (click)="openRefundDialog()" class="pl-action-btn">
        <span class="pl-action-btn-label">{{ 'payments.list.actionRefund' | translate }}</span>
      </ws-button>

      <!-- Riuscito, rimborso parziale già inviato -->
      <div class="pl-action-partial" *ngIf="selectedPayment.partialRefund">
        <ws-button [callToActionType]="'primary'" [width]="'100%'"
          (click)="openRefundDialog()" class="pl-action-btn">
          <span class="pl-action-btn-label">{{ 'payments.list.actionRefundAnother' | translate }}</span>
        </ws-button>
        <span class="pl-residual">
          {{ 'payments.list.residualAmount' | translate }}: {{ formatCents(refundResidualCents,
          selectedPayment.currency) }}
        </span>
      </div>

      <!-- Rimborsato totalmente -->
      <ws-button *ngIf="selectedPayment.displayStatus === 'refunded'" [callToActionType]="'secondary'" [disabled]="true"
        [width]="'100%'" class="pl-action-btn">
        {{ 'payments.list.actionRefunded' | translate }}
      </ws-button>

      <!-- Fallito -->
      <ws-button *ngIf="selectedPayment.displayStatus === 'failed'" [callToActionType]="'secondary'"
        [width]="'100%'" (click)="openErrorDetail()" class="pl-action-btn">
        {{ 'payments.list.actionViewError' | translate }}
      </ws-button>

    </div>
  </div>

  <!-- Refund summary — directly under header -->
  <div class="pl-refund-info-block pl-refund-info-block--header"
    *ngIf="(selectedPayment.displayStatus === 'refunded' || selectedPayment.partialRefund) && selectedPayment.refunds.length > 0">
    <div class="pl-detail-row">
      <span class="pl-detail-label">{{ 'payments.list.refundDate' | translate }}</span>
      <span class="pl-detail-val">{{ formatDate(selectedPayment.refunds[0].created) }}</span>
    </div>
    <div class="pl-detail-row">
      <span class="pl-detail-label">{{ 'payments.list.detailAmount' | translate }}</span>
      <span class="pl-detail-val">{{ formatCents(selectedPayment.refunds[0].amountCents, selectedPayment.currency)
        }}</span>
    </div>
    <div class="pl-detail-row" *ngIf="selectedPayment.refunds[0].reason">
      <span class="pl-detail-label">{{ 'payments.list.refundReason' | translate }}</span>
      <span class="pl-detail-val">{{ refundReasonLabel(selectedPayment.refunds[0].reason) }}</span>
    </div>
    <div class="pl-detail-row">
      <span class="pl-detail-label">{{ 'payments.list.refundId' | translate }}</span>
      <span class="pl-detail-val pl-monospace" [matTooltip]="selectedPayment.refunds[0].id">{{ selectedPayment.refunds[0].id }}</span>
    </div>
  </div>

  <!-- ── Scrollable body ───────────────────────────────────────────────────── -->
  <div class="pl-detail-body">

  <!-- ── Section: Dettagli pagamento ──────────────────────────────────────── -->
  <div class="pl-detail-section">
    <h3 class="pl-section-title">{{ 'payments.list.detailTitle' | translate }}</h3>
    <div class="pl-detail-rows">
      <div class="pl-detail-row">
        <span class="pl-detail-label">{{ 'payments.list.colStatus' | translate }}</span>
        <span class="pl-detail-val"><ws-chip [type]="statusChipType(selectedPayment)"
            [label]="statusLabel(selectedPayment)"></ws-chip></span>
      </div>
      <div class="pl-detail-row">
        <span class="pl-detail-label">{{ 'payments.list.detailDateAcquisition' | translate }}</span>
        <span class="pl-detail-val">{{ formatDate(selectedPayment.created) }}</span>
      </div>
      <div class="pl-detail-row">
        <span class="pl-detail-label">{{ 'payments.list.detailAmount' | translate }}</span>
        <span class="pl-detail-val">{{ formatCents(selectedPayment.amountCents, selectedPayment.currency) }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.amountRefundedCents > 0">
        <span class="pl-detail-label">{{ 'payments.list.refundAmount' | translate }}</span>
        <span class="pl-detail-val pl-amount-refund">-{{ formatCents(selectedPayment.amountRefundedCents, selectedPayment.currency) }}</span>
      </div>

      <!-- Fee expandable block -->
      <div class="pl-fee-block">
        <div class="pl-detail-row pl-detail-row--clickable pl-detail-row--fee" (click)="toggleFee()">
          <span class="pl-detail-label pl-detail-fee-label">
            {{ 'payments.list.detailFee' | translate }}
            <mat-icon class="pl-fee-toggle">{{ feeExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
          </span>
          <span class="pl-detail-val pl-detail-val--fee">{{ formatFeeCents(totalCommissionCents(selectedPayment),
            selectedPayment.currency) }}</span>
        </div>

        <!-- Fee breakdown -->
        <div class="pl-fee-breakdown" *ngIf="feeExpanded">
        <div class="pl-detail-row pl-detail-row--sub" *ngIf="selectedPayment.commissioneWspaymentsCents != null">
          <span class="pl-detail-label">{{ 'payments.list.detailFeeWs' | translate }}</span>
          <span class="pl-detail-val pl-detail-val--fee">{{ formatFeeCents(selectedPayment.commissioneWspaymentsCents,
            selectedPayment.currency) }}</span>
        </div>
        <div class="pl-detail-row pl-detail-row--sub" *ngIf="selectedPayment.commissioneDivineaComCents != null">
          <span class="pl-detail-label">{{ 'payments.list.detailFeeDivinea' | translate }}</span>
          <span class="pl-detail-val pl-detail-val--fee">{{ formatFeeCents(selectedPayment.commissioneDivineaComCents || 0,
            selectedPayment.currency) }}</span>
        </div>
        <div class="pl-detail-row pl-detail-row--sub">
          <span class="pl-detail-label pl-detail-hint">{{ 'payments.list.detailFeeTotal' | translate }}</span>
          <span class="pl-detail-val pl-detail-val--fee">{{ formatFeeCents(totalCommissionCents(selectedPayment),
            selectedPayment.currency) }}</span>
        </div>
        </div>
      </div>

      <div class="pl-detail-row pl-detail-row--net">
        <span class="pl-detail-label">{{ 'payments.list.detailNet' | translate }}</span>
        <span class="pl-detail-val pl-detail-val--net">{{ formatCents(netCents(selectedPayment),
          selectedPayment.currency) }}</span>
      </div>
    </div>
  </div>

  <!-- ── Section: Dettagli vendita (WS badge) ──────────────────────────────── -->
  <div class="pl-detail-section">
    <h3 class="pl-section-title">
      {{ 'payments.list.detailSaleTitle' | translate }}
      <span class="pl-ws-badge">{{ wineryBadgeLabel() }}</span>
    </h3>
    <div class="pl-detail-rows">
      <div class="pl-sale-customer" *ngIf="selectedPayment.customerName || selectedPayment.customerEmail">
        <div class="pl-sale-customer-name" *ngIf="selectedPayment.customerName">{{ selectedPayment.customerName }}</div>
        <a class="pl-sale-customer-email pl-email-link" *ngIf="selectedPayment.customerEmail"
          [href]="'mailto:' + selectedPayment.customerEmail">{{ selectedPayment.customerEmail }}</a>
      </div>
      <div class="pl-detail-row" *ngIf="isRealId(selectedPayment.saleId)">
        <span class="pl-detail-label">{{ 'payments.list.detailSaleId' | translate }}</span>
        <span class="pl-detail-val pl-monospace" [matTooltip]="selectedPayment.saleId">{{ selectedPayment.saleId }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.entityType">
        <span class="pl-detail-label">{{ 'payments.list.detailEntityType' | translate }}</span>
        <span class="pl-detail-val">{{ entityTypeLabel(selectedPayment.entityType) }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="entityDisplayId(selectedPayment) as entityId">
        <span class="pl-detail-label">{{ entityTypeLabel(selectedPayment.entityType) || ('payments.list.detailEntityType' | translate) }}</span>
        <span class="pl-detail-val pl-monospace" [matTooltip]="entityId">{{ entityId }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.origin || selectedPayment.portal || selectedPayment.engine || selectedPayment.shopName">
        <span class="pl-detail-label">{{ 'payments.list.detailOrigin' | translate }}</span>
        <span class="pl-detail-val">{{ originLabel(selectedPayment.origin, selectedPayment.portal, selectedPayment.entityType, selectedPayment.engine, selectedPayment.moto, selectedPayment.shopName) }}</span>
      </div>
    </div>
  </div>

  <!-- ── Section: Modalità di pagamento ────────────────────────────────────── -->
  <div class="pl-detail-section">
    <h3 class="pl-section-title">{{ 'payments.list.detailMethodTitle' | translate }}</h3>
    <div class="pl-detail-rows">
      <div class="pl-detail-row" *ngIf="selectedPayment.cardLast4 || selectedPayment.cardBrand || paymentBrandIcon(selectedPayment)">
        <span class="pl-detail-label">{{ 'payments.list.detailPaymentMethod' | translate }}</span>
        <span class="pl-detail-val pl-payment-method">
          <img *ngIf="paymentBrandIcon(selectedPayment) as icon" class="pl-method-icon" [src]="icon" alt="" />
          {{ paymentMethodLabel(selectedPayment) }}
          <span class="pl-card-brand" *ngIf="selectedPayment.cardBrand && !paymentBrandIcon(selectedPayment)">{{ selectedPayment.cardBrand }}</span>
        </span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.cardLast4">
        <span class="pl-detail-label">{{ 'payments.list.detailCardNumber' | translate }}</span>
        <span class="pl-detail-val">**** {{ selectedPayment.cardLast4 }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="cardExpiry(selectedPayment)">
        <span class="pl-detail-label">{{ 'payments.list.detailCardExpiry' | translate }}</span>
        <span class="pl-detail-val">{{ cardExpiry(selectedPayment) }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.cardBrand">
        <span class="pl-detail-label">{{ 'payments.list.detailCardType' | translate }}</span>
        <span class="pl-detail-val">{{ cardTypeLabel(selectedPayment) }}</span>
      </div>
      <div class="pl-detail-row">
        <span class="pl-detail-label">{{ 'payments.list.detailTransactionMode' | translate }}</span>
        <span class="pl-detail-val pl-transaction-mode">
          <img *ngIf="paymentBrandIcon(selectedPayment) as icon" class="pl-method-icon" [src]="icon" alt="" />
          {{ transactionModeLabel(selectedPayment) }}
        </span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.cardCountry">
        <span class="pl-detail-label">{{ 'payments.list.detailCardCountry' | translate }}</span>
        <span class="pl-detail-val">{{ countryLabel(selectedPayment.cardCountry) }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.cvcCheck">
        <span class="pl-detail-label">{{ 'payments.list.detailCvcCheck' | translate }}</span>
        <span class="pl-detail-val" [class.pl-cvc-pass]="cvcCheckPassed(selectedPayment.cvcCheck)">
          <mat-icon class="pl-cvc-icon" *ngIf="cvcCheckPassed(selectedPayment.cvcCheck)">check_circle</mat-icon>
          {{ cvcCheckLabel(selectedPayment.cvcCheck) }}
        </span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.billingEmail">
        <span class="pl-detail-label">{{ 'payments.list.detailBillingEmail' | translate }}</span>
        <span class="pl-detail-val">{{ selectedPayment.billingEmail }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="selectedPayment.billingAddress">
        <span class="pl-detail-label">{{ 'payments.list.detailBillingAddress' | translate }}</span>
        <span class="pl-detail-val">{{ selectedPayment.billingAddress }}</span>
      </div>
    </div>
  </div>

  <!-- ── Section: Tempistiche ───────────────────────────────────────────────── -->
  <div class="pl-detail-section">
    <h3 class="pl-section-title">{{ 'payments.list.detailTimelineTitle' | translate }}</h3>
    <div class="pl-timeline">
      <div class="pl-timeline-item">
        <div class="pl-timeline-dot pl-timeline-dot--success"></div>
        <div class="pl-timeline-content">
          <span class="pl-timeline-label">{{ 'payments.list.timelineAcquired' | translate }}</span>
          <span class="pl-timeline-time">{{ formatDate(selectedPayment.created) }}</span>
          <span class="pl-timeline-amount">{{ formatCents(selectedPayment.amountCents, selectedPayment.currency)
            }}</span>
        </div>
      </div>
      <!-- Refund events -->
      <div class="pl-timeline-item" *ngFor="let r of selectedPayment.refunds">
        <div class="pl-timeline-dot pl-timeline-dot--refund"></div>
        <div class="pl-timeline-content">
          <span class="pl-timeline-label">{{ 'payments.list.timelineRefund' | translate }}</span>
          <span class="pl-timeline-time">{{ formatDate(r.created) }}</span>
          <span class="pl-timeline-amount pl-amount-refund">-{{ formatCents(r.amountCents, selectedPayment.currency)
            }}</span>
          <span class="pl-timeline-reason" *ngIf="r.reason">{{ refundReasonLabel(r.reason) }}</span>
        </div>
      </div>
    </div>
  </div>

  </div><!-- /.pl-detail-body -->
</div>
</div>


<!-- ═══════════════════════════════════════════════════════════════════════════
     ERROR DETAIL DIALOG
  ═══════════════════════════════════════════════════════════════════════════ -->
<div class="pl-overlay pl-overlay--front" *ngIf="errorDetailOpen" (click)="closeErrorDetail()">
<div class="pl-error-dialog" *ngIf="errorDetailOpen && selectedPayment" role="dialog" (click)="$event.stopPropagation()">
  <div class="pl-refund-header">
    <h3>{{ 'payments.list.errorTitle' | translate }}</h3>
    <button class="pl-modal-close" (click)="closeErrorDetail()"><mat-icon>close</mat-icon></button>
  </div>
  <div class="pl-refund-body">
    <div class="pl-detail-rows" *ngIf="getErrorLabel(selectedPayment.lastErrorCode) as errInfo">
      <div class="pl-detail-row" *ngIf="selectedPayment.lastErrorCode">
        <span class="pl-detail-label">{{ 'payments.list.errorCode' | translate }}</span>
        <span class="pl-detail-val pl-monospace">{{ selectedPayment.lastErrorCode }}</span>
      </div>
      <div class="pl-detail-row">
        <span class="pl-detail-label">{{ 'payments.list.errorDescription' | translate }}</span>
        <span class="pl-detail-val">{{ errInfo.desc }}</span>
      </div>
      <div class="pl-detail-row" *ngIf="errInfo.action">
        <span class="pl-detail-label">{{ 'payments.list.errorAction' | translate }}</span>
        <span class="pl-detail-val">{{ errInfo.action }}</span>
      </div>
    </div>
    <p *ngIf="!getErrorLabel(selectedPayment.lastErrorCode)" class="pl-detail-val">
      {{ selectedPayment.lastErrorMessage || selectedPayment.lastErrorCode || '—' }}
    </p>
  </div>
</div>
</div>